export interface ReviewResponse {
  episode_id: string;
  status: "pending" | "in_review" | "approved" | "rejected" | "changes_requested" | "expired";
  deadline_at: string;
  notes: string;
  preview_urls: Record<string, string>;
  can_submit: boolean;
}

export interface SubmitDecision {
  decision: "approved" | "rejected" | "changes_requested";
  comments?: string[];
  timestamp_markers?: number[];
  general_notes?: string;
}

export interface SessionInfo {
  authenticated: boolean;
  email?: string;
  review_token?: string;
  reason?: string;
}

const API_BASE = import.meta.env.PROD
  ? "https://dfx-reviewer-api.john-defalco.workers.dev/api"
  : "/api";

export function getSessionToken(): string | null {
  return localStorage.getItem("dfx_session");
}

export function setSessionToken(token: string): void {
  localStorage.setItem("dfx_session", token);
}

export function clearSessionToken(): void {
  localStorage.removeItem("dfx_session");
}

function authHeaders(): Record<string, string> {
  const token = getSessionToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export async function checkSession(): Promise<SessionInfo> {
  const res = await fetch(`${API_BASE}/auth/session`, {
    headers: authHeaders(),
  });
  return res.json();
}

export async function fetchReview(token: string): Promise<ReviewResponse> {
  const res = await fetch(`${API_BASE}/reviews/${token}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("UNAUTHORIZED");
    }
    if (res.status === 404) {
      throw new Error("NOT_FOUND");
    }
    if (res.status === 403) {
      throw new Error("FORBIDDEN");
    }
    const data = await res.json().catch(() => ({}));
    if (data.status === "expired") {
      throw new Error("EXPIRED");
    }
    throw new Error(`Failed to fetch review: ${res.status}`);
  }
  return res.json();
}

export async function submitDecision(
  token: string,
  decision: SubmitDecision
): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`${API_BASE}/reviews/${token}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(decision),
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("UNAUTHORIZED");
    }
    if (res.status === 409) {
      throw new Error("ALREADY_DECIDED");
    }
    throw new Error(`Failed to submit decision: ${res.status}`);
  }
  return res.json();
}

export async function resendMagicLink(
  email: string,
  reviewToken: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/resend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, review_token: reviewToken }),
  });
  if (!res.ok) {
    throw new Error(`Failed to resend link: ${res.status}`);
  }
}

export interface CreateTestReviewRequest {
  episode_id?: string;
  reviewer_emails: string[];
  deadline_hours?: number;
  notes?: string;
}

export interface CreateTestReviewResponse {
  success: boolean;
  review_token: string;
  review_url: string;
  deadline_at: string;
  emails_sent: string[];
  emails_failed: string[];
}

export async function createTestReview(
  apiKey: string,
  request: CreateTestReviewRequest
): Promise<CreateTestReviewResponse> {
  const res = await fetch(`${API_BASE}/admin/test-review`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Invalid API key");
    }
    throw new Error(`Failed to create test review: ${res.status}`);
  }
  return res.json();
}

export interface ReviewListItem {
  review_token: string;
  episode_id: string;
  status: string;
  created_at: string;
  deadline_at: string;
  reviewer_emails: string[];
}

export async function listReviews(apiKey: string): Promise<ReviewListItem[]> {
  const res = await fetch(`${API_BASE}/admin/reviews`, {
    headers: { "X-API-Key": apiKey },
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Invalid API key");
    }
    throw new Error(`Failed to list reviews: ${res.status}`);
  }
  const data = await res.json();
  return data.reviews;
}
